/**
 * useTutorLiveCenter — l'activité au centre de la salle du masteur : la zone `center` de
 * `<TutorLivePage>`, ce qu'elle pose par-dessus la salle (`layer`), et le signalement d'une question du
 * quiz (`quizSignal`).
 *
 * Le legacy est `common/liveMode/liveV3/TutorFocusLayoutM.js` (l'activité que montre le centre) et
 * `student/activity/todo/TodoModal.js` monté par lui (`noModal`, branche tuteur). Ce crochet compose :
 *  - **le choix de l'activité** (`TFL:47-106`) : quand on regarde un élève, ou qu'il change d'activité,
 *    celle qu'il a devant lui, sinon la première de ses faites, sinon sa « à faire » verrouillée la plus
 *    récente ; la demande « Voir le rendu » du fil (`studentActivityFocusRequest`, dont le numéro croît) ;
 *    l'activité ouverte par la pellicule (`selection.openActivity`) ; une activité sortie des listes ferme
 *    le centre ;
 *  - **l'activité présentée** (`presentedActivity`) : pendant une présentation, son départ et son arrêt,
 *    le centre garde l'élève et l'activité présentés, quelle que soit la sélection — les élèves voient la
 *    région, toucher un autre élève n'y met jamais sa vidéo ni son prénom (l'ancien `TodoModal`
 *    recouvrait la salle) ;
 *  - **l'onglet** (`tutorLiveCenterTabReducer`) ;
 *  - **les relectures** : `getActivities({ with_logs: 1, activity_ids, include_archived: 0 })` à
 *    l'ouverture, à chaque autre activité et à chaque photo (les photos du rendu viennent de ses journaux),
 *    et toutes les 5 s pendant 90 s tant que la correction se prépare (`useCorrectionPolling`) ;
 *  - **les gestes** (`useTutorLiveCenterActions`), **les avis et « Signaler »**
 *    (`useTutorLiveCenterReviews`), **le visuel et l'aide à la correction** (`useTutorLiveCenterMaterial`),
 *    **le dessin** (`useTutorLiveCenterAnnotation`), **l'écoute du rendu oral** (`useVoicePlayer`) ;
 *  - **« Présenter »** : la présentation (`presentation`) et sa barre de dessin ; toucher « Tableau blanc »
 *    hors présentation la lance (`TodoModal:1593-1598`) ; « Afficher à l'élève » reste à côté de la barre
 *    (C-113) ; la correction se cache d'elle-même (`<TutorLiveActivityView presenting>`) ;
 *  - **« Interrompre la présentation ? »** (`TutorFocusLayoutM.confirmInterruptPresentation`, web) avant
 *    de fermer l'activité ou d'ouvrir « Contrôles » pendant une présentation : « Interrompre » l'arrête, le
 *    geste suit 300 ms après ;
 *  - **la ligne de l'activité** (`ActivityItemInfo`) : le chapitre (à défaut la matière) et le chrono, orange
 *    au-delà du temps prévu ;
 *  - **« Connexion faible »** (`ConnectionQualityAlert`) : les alertes du centre, lues dans les tranches
 *    `liveMode` qu'écrit l'écouteur global (après ses 15 s de dégradation) ;
 *  - **ce que le public ne voit pas** (`presentationAudienceNote`) : pendant une présentation, combien
 *    d'élèves présents sont en Concentré (ils ne voient pas le partage), et « Tous en Dialogue », le geste
 *    même de la barre (`onAllDialogue`) — sans prénom, la ligne partant aussi au centre seul.
 *
 * Signature du contrat (`tutorLiveContract.ts`), plus un 5ᵉ paramètre facultatif : `onGenerateFrom`, que le
 * fil fournit (la génération y est portée) pour « Générer... » ; absent, le geste n'est pas offert ; et
 * `onAllDialogue`, que la barre fournit ; absent, la ligne du public n'offre pas de geste.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useWindowDimensions } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { cleanMediaUrl } from "../../../../../../data/constants/apiConstants"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { useCorrectionPolling } from "../../../../../../engine/activity/correction/useCorrectionPolling"
import { apiCall, apiFailureOf, type ApiService } from "../../../../../../engine/shared/apiCall"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import {
    type TutorLiveCenterZone,
    type TutorLiveOverlaysZone,
} from "../../../../../../design-system/pages/TutorLivePage/TutorLivePage"
import { type TutorLiveActivityHandlers } from "../../../../../../design-system/organisms/TutorLiveActivityView/TutorLiveActivityView"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { useVoicePlayer } from "../../../hooks/useVoicePlayer"
import { type UITutorLiveCenterTab, type UITutorLiveStudent } from "../../../types/tutorLive"
import {
    defaultLiveActivityId,
    findLiveActivity,
    liveActivityHasWhiteboard,
    liveActivityHasWork,
    type ApiLiveStudent,
} from "../../../../adapters/tutor/Live/tutorLiveActivityAdapter"
import {
    toStudentAssessments,
    type ApiStudentAssessment,
} from "../../../../adapters/student/Assessments/studentAssessmentsAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import {
    currentActivityOf,
    findLiveStudent,
    initialTutorLiveCenterTab,
    isStudentOnline,
    liveActivityInfo,
    isStudentWithHelper,
    liveActivityRequiresQuiz,
    presentationAudienceNote,
    presentedActivity,
    type PresentedActivity,
    selectLiveCenterSlices,
    tutorLiveCenterActions,
    tutorLiveCenterFocus,
    tutorLiveConnectionAlerts,
    tutorLiveCenterTabReducer,
} from "../../../../view-models/tutor/Live/tutorLiveCenterViewModel"
import { seenActivityOf } from "../../../../view-models/tutor/Live/tutorLiveStudentsViewModel"
import {
    type TutorLiveLayer,
    type TutorLivePresentation,
    type TutorLiveRoomContext,
    type TutorLiveSelection,
} from "./tutorLiveContract"
import { type TutorLiveGenerateSource, useTutorLiveCenterActions } from "./useTutorLiveCenterActions"
import { type AnnotationView, useTutorLiveCenterAnnotation } from "./useTutorLiveCenterAnnotation"
import { useTutorLiveCenterMaterial } from "./useTutorLiveCenterMaterial"
import { useTutorLiveCenterReviews } from "./useTutorLiveCenterReviews"

export type { TutorLiveGenerateSource } from "./useTutorLiveCenterActions"
export { TUTOR_LIVE_CENTER_EQUIVALENCE } from "./useTutorLiveCenterActions"

export type UseTutorLiveCenterOptions = {
    /** « Générer à partir de l'exo » : le fil ouvre sa génération sur cette source. Absent : pas de geste. */
    onGenerateFrom?: (source: TutorLiveGenerateSource) => void
    /**
     * « Tous en Dialogue » : le geste de la barre (« Plus » → « Tous en Dialogue », `setSoundModeV2
     * { enabled: true, user_ids }`), offert sous la tête pendant une présentation quand des élèves présents
     * sont en Concentré. Absent : la ligne seule.
     */
    onAllDialogue?: () => void
}

export type TutorLiveCenterResult = {
    zone: TutorLiveCenterZone
    layer: TutorLiveLayer
    quizSignal: TutorLiveOverlaysZone["quizSignal"]
    /** « Contrôles » au centre (le prochain contrôle d'un élève, `students.onOpenAssessments`). */
    openAssessments: (studentId: string) => void
    /** L'activité ouverte au centre, de quoi en faire une semblable (« /similaire » du fil) ; `null` sinon. */
    similarSource: () => TutorLiveGenerateSource | null
}

/** Sous cette largeur, le centre est un téléphone : sans activité, il montre la liste (C-05). */
const PHONE_BELOW = 600
/** Une snackbar d'accusé tient ce temps (l'ancien : 3 à 4 s). */
const SNACKBAR_MS = 4000

const LOAD_FAILED = "L'activité n'a pas pu être relue. Réessaie dans un instant."
/** « Interrompre » : le geste suit l'arrêt de la présentation de ce délai (`TutorFocusLayoutM.js:118-121`). */
export const AFTER_INTERRUPT_MS = 300

type Snack = { message: string; tone: "success" | "danger" | "info" }

/* eslint-disable @typescript-eslint/no-explicit-any */
const selectSubjects = (state: any) =>
    state?.data?.staticData?.school_subjects as { id?: unknown; name?: string }[] | undefined
const selectTemplates = (state: any) => state?.data?.staticData?.activity_templates as { id?: unknown }[] | undefined
const selectPending = (state: any) => state?.data?.pendingAttachments?.byActivityId as unknown
const selectAssessments = (state: any) =>
    state?.data?.schoolAssessments?.byStudentId as Record<string, ApiStudentAssessment[] | null | undefined> | undefined
const selectMeId = (state: any) => ((selectAccount(state) as any)?.id ?? null) as number | null
const NO_IDS: unknown[] = []
const selectPoorIds = (state: any) => (state?.liveMode?.poorConnectionQualityIds ?? NO_IDS) as unknown[]
const selectLocalPoor = (state: any) => state?.liveMode?.isLocalPoorConnection === true
/* eslint-enable @typescript-eslint/no-explicit-any */

const findById = <T extends { id?: unknown }>(list: T[] | undefined, id: unknown): T | undefined =>
    id == null ? undefined : list?.find((item) => item?.id != null && String(item.id) === String(id))

export function useTutorLiveCenter(
    room: TutorLiveRoomContext,
    selection: TutorLiveSelection,
    presentation: TutorLivePresentation,
    students: UITutorLiveStudent[],
    options: UseTutorLiveCenterOptions = {}
): TutorLiveCenterResult {
    const slices = useSelector(selectLiveCenterSlices)
    const subjects = useSelector(selectSubjects)
    const templates = useSelector(selectTemplates)
    const pendingByActivity = useSelector(selectPending)
    const assessmentsByStudent = useSelector(selectAssessments)
    const meId = useSelector(selectMeId)
    const poorIds = useSelector(selectPoorIds)
    const localPoor = useSelector(selectLocalPoor)
    const { width } = useWindowDimensions()
    const phone = width < PHONE_BELOW

    const selectedId = selection.selectedId ?? null
    const selectedStudent: ApiLiveStudent | null = findLiveStudent(slices.students, selectedId)
    const presentingRef = useRef(presentation.on)
    presentingRef.current = presentation.on

    // ── L'activité que montre le centre (`TutorFocusLayoutM`) ──────────────────────────────────────
    const [chosen, setChosen] = useState<string | null>(null)
    const skipDefaultRef = useRef(false)
    const studentCurrent = currentActivityOf(slices, selectedId)

    // Regarder un élève, ou qu'il change d'activité : l'activité par défaut (`TFL:77-88`).
    const studentRef = useRef(selectedStudent)
    studentRef.current = selectedStudent
    useEffect(() => {
        if (selectedId == null) {
            setChosen(null)
            return
        }
        if (skipDefaultRef.current) {
            skipDefaultRef.current = false
            return
        }
        const next = defaultLiveActivityId(studentRef.current, studentCurrent)
        // Pendant une présentation, une activité affichée le reste : toucher un élève, ou qu'il change lui-même
        // d'activité, ne change pas ce que la présentation montre. Seule une activité ouverte (la pellicule, la
        // liste, le fil) le change (`presentedActivity`).
        setChosen((shown) => (presentingRef.current && shown != null ? shown : next))
    }, [selectedId, studentCurrent])

    // La pellicule ouvre une activité (`students.onOpenActivity` → `selection.openActivityFor`).
    const opened = selection.openActivity
    useEffect(() => {
        if (opened != null && opened.studentId === selectedId) setChosen(opened.activityId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened])

    // « Voir le rendu / l'énoncé » du fil : honorée à chaque demande, dès que l'élève est regardé.
    const handledRequestRef = useRef(0)
    const request = slices.focusRequest
    useEffect(() => {
        if (request?.requestId == null || request.requestId === handledRequestRef.current) return
        if (selectedId == null || String(request.studentId) !== selectedId) return
        handledRequestRef.current = request.requestId
        setChosen(request.activityId != null ? String(request.activityId) : null)
    }, [request, selectedId])

    const selectedRaw = findLiveActivity(selectedStudent, chosen)

    // Pendant une présentation (son départ et son arrêt compris), l'élève et l'activité présentés restent,
    // quelle que soit la sélection : relus à chaque rendu, d'où la ref (une valeur dérivée, idempotente).
    const presentedRef = useRef<PresentedActivity | null>(null)
    const presented = presentedActivity(presentedRef.current, {
        presenting: presentation.on || presentation.pending,
        student: selectedStudent,
        raw: selectedRaw,
        students: slices.students,
    })
    presentedRef.current = presented
    const student = presented?.student ?? selectedStudent
    const raw = presented?.raw ?? selectedRaw
    const centerStudentId = presented?.student.id != null ? String(presented.student.id) : selectedId
    const activityId = raw?.id != null ? String(raw.id) : null

    const close = useCallback(() => {
        setChosen(null)
        selection.closeActivity()
    }, [selection])

    // « Interrompre la présentation ? » (`TutorFocusLayoutM.confirmInterruptPresentation`) : au web, pendant
    // une présentation, quitter la vue la demande ; « Interrompre » l'arrête, le geste suit 300 ms après.
    const [interrupting, setInterrupting] = useState<{ then: () => void } | null>(null)
    const afterInterruptRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(
        () => () => {
            if (afterInterruptRef.current != null) clearTimeout(afterInterruptRef.current)
        },
        []
    )
    const confirmInterrupt = useCallback((then: () => void) => {
        if (!presentingRef.current || !IS_WEB) {
            then()
            return
        }
        setInterrupting({ then })
    }, [])

    // L'activité est sortie des listes de l'élève (archivée ailleurs) : le centre se ferme ; une présentation
    // garde la sienne (`presentedActivity`) et le centre se ferme à son arrêt.
    useEffect(() => {
        if (chosen != null && selectedStudent != null && selectedRaw == null && !presentation.on) setChosen(null)
    }, [chosen, selectedStudent, selectedRaw, presentation.on])

    // ── L'onglet ───────────────────────────────────────────────────────────────────────────────────
    const [tabState, dispatchTab] = useReducer(tutorLiveCenterTabReducer, initialTutorLiveCenterTab)
    const hasWork = liveActivityHasWork(raw)
    const hasQuiz = raw?.has_prerequisite_quiz === true || raw?.has_prerequisite_quiz === 1
    const mediaCount = raw?.media_count ?? 0
    useEffect(() => {
        dispatchTab({
            type: "activity",
            studentId: centerStudentId,
            activityId,
            hasWork,
            hasQuiz,
            mediaCount,
            none: phone ? "activities" : "visio",
        })
    }, [centerStudentId, activityId, hasWork, hasQuiz, mediaCount, phone])

    // ── Les relectures de l'activité ───────────────────────────────────────────────────────────────
    const [snack, setSnack] = useState<Snack | null>(null)
    const notify = useCallback((message: string, tone: Snack["tone"]) => setSnack({ message, tone }), [])
    useEffect(() => {
        if (snack == null) return
        const timer = setTimeout(() => setSnack(null), SNACKBAR_MS)
        return () => clearTimeout(timer)
    }, [snack])

    const refreshRef = useRef<() => void>(() => undefined)
    const dispatch = useDispatch() as unknown as (action: never) => unknown
    refreshRef.current = () => {
        if (activityId == null) return
        apiCall(dispatch, activitiesService.getActivities as ApiService, {
            urlParams: { with_logs: 1, activity_ids: activityId, include_archived: 0 },
        }).catch((cause) => notify(apiErrorMessage(apiFailureOf(cause), LOAD_FAILED), "danger"))
    }
    const refresh = useCallback(() => refreshRef.current(), [])
    useEffect(() => {
        refresh()
    }, [activityId, mediaCount, refresh])
    useCorrectionPolling(raw?.status === "pending_review" || raw?.correction_generating === true, refresh)

    // ── Ce qui accompagne l'activité ───────────────────────────────────────────────────────────────
    const online = student?.id != null && isStudentOnline(slices, student.id)
    // La lecture des tuiles : rien hors de la salle, `0` n'est aucune activité.
    const seen = student?.id != null ? seenActivityOf(slices, student.id, online) : null
    const shown = online && seen != null && activityId != null && seen === activityId
    const subject = findById(subjects, raw?.school_subject_id)

    const actions = useTutorLiveCenterActions({
        room,
        student,
        raw,
        shown,
        withHelper: student?.id != null && isStudentWithHelper(slices, student.id),
        meId,
        known: {
            activityTemplateId: findById(templates, raw?.activity_template_id)?.id as string | number | undefined,
            schoolSubjectId: subject?.id as string | number | undefined,
        },
        pendingAttachments: pendingByActivity,
        refresh,
        close,
        notify,
        onGenerateFrom: options.onGenerateFrom,
    })
    const reviews = useTutorLiveCenterReviews(raw, notify)
    const whiteboard = liveActivityHasWhiteboard(raw)
    const material = useTutorLiveCenterMaterial({
        material: raw?.material,
        presenting: presentation.on,
        whiteboard,
        writtenStatement: raw?.material != null && !raw.material.url && (raw.material.attachments ?? []).length === 0,
    })

    // La vue montrée, pour « Annuler » et « Tout effacer ».
    const view: AnnotationView =
        tabState.tab === "whiteboard"
            ? "whiteboard"
            : tabState.tab === "statement" || tabState.tab === "work"
              ? tabState.split && hasWork
                  ? "split"
                  : tabState.tab
              : "other"
    const drawing = useTutorLiveCenterAnnotation({ activityId, presenting: presentation.on, view })

    // L'écoute du rendu oral (l'ancien `AudioRecordingContainer readOnly`).
    const focus = useMemo(
        () =>
            tutorLiveCenterFocus({
                student,
                activityId,
                subjectName: subject?.name ?? null,
                seenActivityId: seen,
                online,
                cleanUrl: cleanMediaUrl,
                correctionReview: reviews.correctionReview,
                quizFeedback: reviews.quizFeedback,
                visual: material.visual,
                statementAid: material.statementAid,
            }),
        [
            student,
            activityId,
            subject?.name,
            seen,
            online,
            reviews.correctionReview,
            reviews.quizFeedback,
            material.visual,
            material.statementAid,
        ]
    )
    const audioUri = focus?.work?.audioUri ?? null
    const recordingId = activityId != null ? `work-${activityId}` : null
    const player = useVoicePlayer(audioUri != null ? recordingId : null)

    // ── Les gestes de la vue ───────────────────────────────────────────────────────────────────────
    const onChangeTab = useCallback(
        (tab: UITutorLiveCenterTab) => {
            // La visio : l'activité se ferme (la croix de l'ancienne fiche).
            if (tab === "visio") {
                confirmInterrupt(close)
                return
            }
            dispatchTab({ type: "choose", tab })
            if (tab === "whiteboard" && !presentingRef.current) presentation.start()
        },
        [close, confirmInterrupt, presentation]
    )

    const openAssessments = useCallback(
        (studentId: string) =>
            confirmInterrupt(() => {
                if (selection.selectedId !== studentId) {
                    skipDefaultRef.current = true
                    selection.select(studentId)
                }
                setChosen(null)
                selection.closeActivity()
                dispatchTab({ type: "assessments", studentId })
            }),
        [selection, confirmInterrupt]
    )

    const interruptDialog: TutorLiveLayer["dialog"] =
        interrupting != null
            ? {
                  value: { kind: "interrupt_presentation" },
                  onConfirm: () => {
                      setInterrupting(null)
                      presentation.stop()
                      afterInterruptRef.current = setTimeout(interrupting.then, AFTER_INTERRUPT_MS)
                  },
                  onCancel: () => setInterrupting(null),
                  onDismiss: () => setInterrupting(null),
              }
            : null

    // « Connexion faible » (`ConnectionQualityAlert`) : la mienne, celle des élèves présents.
    const alerts = useMemo(
        () => tutorLiveConnectionAlerts({ localPoor, poorIds, slices }),
        [localPoor, poorIds, slices]
    )

    const activityHandlers: TutorLiveActivityHandlers = {
        onGenerateVisual: material.onGenerateVisual,
        onToggleVisual: material.onToggleVisual,
        onVisualFeedback: material.onVisualFeedback,
        onCorrectionReview: reviews.onCorrectionReview,
        onQuizFeedback: reviews.onQuizFeedback,
        onSignalQuestion: reviews.onSignalQuestion,
        onToggleAid: material.onToggleAid,
        onRetryAid: material.onRetryAid,
        onAidFeedback: material.onAidFeedback,
        audio:
            audioUri != null && recordingId != null
                ? {
                      playing: player.messageId === recordingId && player.playing,
                      progress: player.messageId === recordingId ? player.progress : 0,
                      onTogglePlay: () => player.toggle(recordingId, audioUri),
                  }
                : undefined,
        whiteboard: drawing.whiteboard,
    }

    // Pendant une présentation, ce que les élèves en Concentré ne voient pas (le compte, jamais un prénom).
    const audience = presentationAudienceNote(students, presentation.on)
    const onAllDialogue = options.onAllDialogue

    const assessments =
        tabState.tab === "assessments" && selectedId != null
            ? { items: toStudentAssessments(assessmentsByStudent?.[selectedId] ?? []) }
            : undefined

    const zone: TutorLiveCenterZone = {
        tab: tabState.tab,
        onChangeTab,
        split: tabState.split,
        onToggleSplit: () => dispatchTab({ type: "toggle_split" }),
        focus,
        actions: tutorLiveCenterActions(raw, { online, canGenerate: actions.canGenerate }),
        activityInfo: liveActivityInfo(raw, subject?.name ?? null),
        onAction: actions.onAction,
        submitting: actions.submitting,
        showPressableWhenShown: true,
        present: {
            on: presentation.on,
            pending: presentation.pending,
            onToggle: presentation.on ? presentation.stop : presentation.start,
            annotation: drawing.toolbar,
            keepShow: true,
            audienceNote:
                audience != null
                    ? {
                          text: audience.text,
                          action:
                              onAllDialogue != null
                                  ? { label: audience.actionLabel, onPress: onAllDialogue }
                                  : undefined,
                      }
                    : undefined,
        },
        centerOnly: presentation.centerOnly,
        activityHandlers,
        quizFirst: liveActivityRequiresQuiz(raw) ? actions.quizFirst : null,
        annotation: drawing.annotation,
        shareRegionRef: presentation.shareRegionRef,
        assessments,
        alerts,
    }

    const layer: TutorLiveLayer = {
        // La garde est un geste du masteur : elle passe devant, non marquée automatique.
        dialog: interruptDialog ?? actions.dialog,
        dialogPriority: 1,
        snackbar: snack != null ? { value: snack, onDismiss: () => setSnack(null) } : null,
        sheet: actions.sheet ?? undefined,
    }

    return { zone, layer, quizSignal: reviews.quizSignal, openAssessments, similarSource: actions.similarSource }
}
